import { useNavigate, useLocation } from 'react-router-dom';
import { useState , useEffect } from 'react';
import { ArrowLeft, LoaderCircle } from 'lucide-react';
import { useGlobalState } from '../../utils/GlobalState';
import { submitIssueDetection } from '../../services/apiService';
import Swal from 'sweetalert2';
import { decodedToken } from '../../utils/auth';

const AgreeJudgement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: locationState = {} } = location;
  const { suggestion: initialSuggestion = '', imageSrc, showAnalysis, analysisResult, predictions, rawImageFile } = locationState;
  const { state, updateState } = useGlobalState();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get email and org_name from JWT token
  const decoded = decodedToken();
  const userEmail = decoded?.email || '';
  const companyInfo = decoded?.companyInfo || 'Unknown';

  // Use global state for suggestion, sentToEmail, predictions, and predictedImageUrl
  const suggestion = state.suggestion || initialSuggestion;
  const sentToEmail = state.sentToEmail || '';
  const savedPredictions = Array.isArray(state.predictions)
    ? state.predictions
    : Array.isArray(predictions)
    ? predictions
    : Array.isArray(analysisResult?.data?.predictions)
    ? analysisResult.data.predictions
    : [];
  const savedRawImageFile = state.rawImageFile || rawImageFile || null;

  const latestFormData = state.formData && state.formData.length > 0 ? state.formData[state.formData.length - 1] : null;

  useEffect(() => {
    const backState = {
      uploadedImage: imageSrc,
      showAnalysis,
      analysisResult,
      predictions: savedPredictions,
      rawImageFile: savedRawImageFile,
      suggestion: '',
      sentToEmail: userEmail,
    };
    updateState(backState);
  }, []);

  const handleBack = () => {
    const backState = {
      uploadedImage: imageSrc,
      showAnalysis,
      analysisResult,
      predictions: savedPredictions,
      rawImageFile: savedRawImageFile,
      suggestion: '',
      sentToEmail: '',
    };
    updateState(backState);
    
    navigate('/upload-image-detection', { state: backState });
  };

  const handleSaveAndSend = async () => {
  setIsLoading(true);
  try {
    if (!sentToEmail) {
      throw new Error('Recipient email is required');
    }
    if (!userEmail) {
      throw new Error('User email is required');
    }
    if (!savedRawImageFile) {
      throw new Error('Original image file is required');
    }
    if (!Array.isArray(savedPredictions) || savedPredictions.length === 0) {
      console.warn('No valid predictions available; proceeding with empty predictions');
    }

    let highestRootCause = null;
    if (analysisResult?.percentages?.rootCauses) {
      const rootCauses = analysisResult.percentages.rootCauses;
      highestRootCause = Object.keys(rootCauses).reduce((highest, key) => {
        const percentage = parseFloat(rootCauses[key].percentage) || 0;
        if (!highest || percentage > highest.percentage) {
          return { rootCauseType: key, percentage };
        }
        return highest;
      }, null)?.rootCauseType;
    }

    const formattedPredictions = savedPredictions.map((pred, index) => {
      if (pred.class_id === undefined || pred.class_id === null) {
        console.warn(`Missing class_id in prediction ${index}:`, pred);
        throw new Error(`Prediction ${index + 1} is missing a class_id. Please ensure the image analysis includes valid class IDs.`);
      }
      return {
        issueId: String(pred.class_id),
        issueType: pred.class_name?.toLowerCase().replace(/\s+/g, '_') || '',
        rootCauseType: highestRootCause || pred.rootCauseType || null, 
        issueDetail: pred.issueDetail || null,
        confidence: pred.confidence !== undefined ? pred.confidence : 0,
        box: pred.bbox || pred.box || [0, 0, 0, 0],
        coordinates: {
          x: pred.bbox?.[0] || pred.coordinates?.x1 || 0,
          y: pred.bbox?.[1] || pred.coordinates?.y1 || 0,
          width: pred.bbox ? pred.bbox[2] - pred.bbox[0] : pred.coordinates?.x2 - pred.coordinates?.x1 || 0,
          height: pred.bbox ? pred.bbox[3] - pred.bbox[1] : pred.coordinates?.y2 - pred.coordinates?.y1 || 0,
        },
        predictedImageUrl: pred.predictedImageUrl || locationState.predictedImageUrl || analysisResult?.data?.predicted_image_url,
      };
    });

    let transformedDimensions = null;
    if (latestFormData?.dimensions) {
      try {
        const parseDimension = (dim) => {
          if (!dim || typeof dim !== 'string') {
            throw new Error(`Invalid dimension value: ${dim}`);
          }
          const [whole, decimal] = dim.split('.').map(Number);
          if (!Number.isFinite(whole) || !Number.isFinite(decimal)) {
            throw new Error(`Invalid dimension format: ${dim}`);
          }
          return { whole, decimal };
        };
        transformedDimensions = {
          width: parseDimension(latestFormData.dimensions.width.input || latestFormData.dimensions.width),
          height: parseDimension(latestFormData.dimensions.height.input || latestFormData.dimensions.height),
        };
      } catch (error) {
        console.warn('Failed to transform dimensions:', {
          message: error.message,
          dimensions: latestFormData.dimensions,
        });
        Swal.fire({
          icon: "error",
          title: "Invalid Dimensions",
          text: `Failed to process dimensions: ${error.message}. Please ensure dimensions are in the format "whole.decimal" (e.g., "708.0").`,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
        setIsLoading(false);
        return;
      }
    }

    const productInfo = latestFormData
      ? {
          companyInfo: latestFormData.companyInfo || companyInfo.trim(),
          productType: latestFormData.productType || null,
          brand: latestFormData.brand,
          modelName: latestFormData.modelName || null,
          bezel: latestFormData.bezel || null,
          dimensions: transformedDimensions,
          screenType: latestFormData.screenType || null,
          inchSize: latestFormData.inchSize || null,
        }
      : null;

    const formData = new FormData();
    formData.append('email', userEmail);
    formData.append('dataFromStatus', 'Detection_agree');
    formData.append('productInfo', JSON.stringify(productInfo));
    formData.append('predictions', JSON.stringify(formattedPredictions));
    formData.append('feedbackRemark', suggestion);
    formData.append('sentToEmail', sentToEmail);
    formData.append('rawimage', savedRawImageFile);
    const response = await submitIssueDetection(formData);

    await Swal.fire({
      title: 'Success!',
      text: `Agreed Product and Suggestions sent to ${sentToEmail} successfully!`,
      icon: 'success',
      confirmButtonText: 'OK',
      background: 'white',
      customClass: {
        confirmButton: 'bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700',
        title: 'text-white',
        content: 'text-gray-300',
      },
    });

    updateState({
      uploadedImage: null,
      rawImageFile: null,
      predictions: [],
      suggestion: '',
      sentToEmail: '',
      showAnalysis: false,
      analysisResult: null,
      predictedImageUrl: null,
      formData: [],
    });

    navigate('/productinfodetection', { state: { reset: true } });
  } catch (err) {
    console.error('Save and Send Error Details:', {
      message: err.message,
      stack: err.stack,
      response: err.response,
      details: err,
    });
    setError(err.message || 'Failed to submit issue detection data');
    await Swal.fire({
      title: 'Error!',
      text: err.message || 'Failed to submit issue detection data',
      icon: 'error',
      confirmButtonText: 'OK',
      background: 'white',
      customClass: {
        confirmButton: 'bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700',
        title: 'text-white',
        content: 'text-gray-300',
      },
    });
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="w-full min-h-screen bg-black text-white flex items-center justify-center p-2 sm:p-4">
      <div className="w-full bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBack}
            className="p-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition"
            title="Back"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className="text-xl font-bold ml-4">Provide Suggestion and Save</h2>
        </div>
        {error && (
          <div className="mb-4 p-2 bg-red-600 text-white rounded">
            {error}
          </div>
        )}
        <div className="mb-8">
          <label className="block mb-2 text-lg font-semibold text-gray-300">Suggestion</label>
          <textarea
            className="w-full p-2 bg-gray-700 rounded-xl mb-4 text-gray-300"
            value={suggestion}
            onChange={(e) => updateState({ suggestion: e.target.value })}
            placeholder="Enter your suggestion(optional)"
          ></textarea>
          <label className="block mb-2 text-lg font-semibold text-gray-300">Recipient Email</label>
          <input
            type="email"
            className="w-full p-2 bg-gray-700 rounded mb-4 text-gray-300"
            value={sentToEmail}
            onChange={(e) => updateState({ sentToEmail: e.target.value })}
            placeholder="e.g., example@tpv-tech.com"
          />
        </div>
        <div className="mt-6">
          <button
            onClick={handleSaveAndSend}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center"
            disabled={!sentToEmail || !savedRawImageFile || isLoading}
          >
            {isLoading ? (
              <>
                <LoaderCircle className="w-5 h-5 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              'Save and Send Result'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgreeJudgement;

